import React, { Fragment, useState, useEffect } from "react";
import { canEditPartFor } from "utils/partAccess";
import { buildServicePayload } from "utils/buildServicePayload";
import { makeStyles, withStyles } from "@mui/styles";
import clsx from "clsx";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import FeaturedPlayListIcon from "@mui/icons-material/FeaturedPlayList";
import DashboardIcon from "@mui/icons-material/Dashboard";
import GrainIcon from "@mui/icons-material/Grain";
import StepConnector from "@mui/material/StepConnector";
import Card from "@mui/material/Card";
import { useGetSpecificRoleQuery } from "services/roles";
import AssessmentIcon from "@mui/icons-material/Assessment";
import Details from "components/ServiceCreator/Details";
import MonitoringControls from "components/ServiceCreator/MonitoringControls";
import Dashboard from "components/ServiceCreator/Dashboard";
import Layout from "components/ServiceCreator/Layout";
import AssetComp from "components/ServiceCreator/Asset";
import { useSelector, useDispatch } from "react-redux";
import InfoIcon from "@mui/icons-material/Info";
import Tooltip from "@mui/material/Tooltip";
import Zoom from "@mui/material/Zoom";
import { setService, resetService } from "rtkSlices/ServiceCreatorSlice";
import { useEditServiceMutation } from "services/services";
import { useUpdateServicesMutation } from "services/roles";
import { useSnackbar } from "notistack";
import Keys from "Keys";
import { setRole } from "rtkSlices/metaDataSlice";
import { waitForSolutionInRole } from "utils/waitForSolutionInRole";
import SettingsIcon from "@mui/icons-material/Settings";
import { useCreateServiceMutation } from "services/services";

function ColorlibStepIcon(props) {
  const metaDataValue = useSelector((state) => state.metaData);
  const useColorlibStepIconStyles = makeStyles({
    fab: {
      position: "fixed",
      bottom: "40px",
      right: "40px",
      zIndex: 20,
    },
    root: {
      backgroundColor: "#ccc",
      zIndex: 1,
      color: "#fff",
      width: 50,
      height: 50,
      display: "flex",
      borderRadius: "50%",
      justifyContent: "center",
      alignItems: "center",
      position: "relative",
    },
    active: {
      backgroundImage: `linear-gradient( 136deg, ${metaDataValue.branding.secondaryColor} 0%, ${metaDataValue.branding.secondaryColor} 50%, ${metaDataValue.branding.secondaryColor} 100%)`,
      boxShadow: "0 4px 10px 0 rgba(0,0,0,.25)",
    },
    completed: {
      backgroundImage: `linear-gradient( 136deg, ${metaDataValue.branding.secondaryColor} 0%, ${metaDataValue.branding.secondaryColor} 50%, ${metaDataValue.branding.secondaryColor} 100%)`,
    },
  });

  const classes = useColorlibStepIconStyles();
  const { active, completed } = props;
  const icons = {
    1: <FeaturedPlayListIcon />,
    2: <GrainIcon />,
    3: <AssessmentIcon />,
    4: <SettingsIcon />,
    5: <DashboardIcon />,
  };

  return (
    <div
      className={clsx(classes.root, {
        [classes.active]: active,
        [classes.completed]: completed,
      })}
    >
      {icons[String(props.icon)]}
    </div>
  );
}

const useStyles = makeStyles({
  root: {
    width: "100%",
  },
  button: {
    marginRight: "10px",
  },
  instructions: {
    marginTop: "10px",
    marginBottom: "10px",
  },
});

export default function CustomizedSteppers(props) {
  const metaDataValue = useSelector((state) => state.metaData);
  const serviceValue = useSelector((state) => state.serviceCreator);

  // P2c: per-part edit gating. A non-⊤ caller may edit only Solution-Management parts held at ALL
  // (metaData.partPermissions, shipped in P2b). ⊤ (unscopedAdmin) bypasses. The authoritative FE
  // enforcement is the field-by-field save-filter in handleSave (mirrors the BE allow-list); the
  // banner below is only a warning. Duplication (create-from-copy) is full authorship — never filtered.
  // The BE gates the whole editService on Solution Edit=ALL (the umbrella), then per-part. Mirror
  // that: a non-⊤ caller needs Solution Edit=ALL for ANY part to be editable. Solution Edit is
  // a page-tab (appPaths).
  // Editing rights come from the role-level Solution Edit parts and apply to every solution
  // the role holds (canEditPartFor handles ⊤ bypass, the umbrella, the global Thresholds
  // Management page-tab, and the legacy pre-migration fallback).
  const partHeld = (part) =>
    canEditPartFor(metaDataValue, serviceValue.serviceId, part);
  // Read-only warning banner. IMPORTANT: the stepper's controls do NOT map 1:1 to parts — several
  // single controls write fields owned by OTHER parts (the actuator toggle writes assetMapping/Asset;
  // the energy popup writes Configurations+Key-Info; the map-identifier select writes layout/Layout;
  // Color-Spectrum writes dataPointThresholds/Thresholds), so a per-STEP banner silently misses those
  // cross-part writes. Instead warn whenever the caller is a partial-permission admin (holds the
  // umbrella but not EVERY part) — shown on every step, so it can never miss a cross-part drop. Precise
  // per-control disabling is P2c v2. ⊤ / all-parts / duplication → no banner. Monitoring & Value
  // Insights share configuredSensors, so holding either satisfies the sensor part.
  const holdsAllParts =
    partHeld("Key Info") &&
    partHeld("Asset") &&
    (partHeld("Monitoring") || partHeld("Value Insights")) &&
    partHeld("Controlling") &&
    partHeld("Configurations") &&
    partHeld("Layout") &&
    partHeld("Threshold Management");
  const currentStepReadOnly = !props.duplicationMode && !holdsAllParts;
  // P2c v2: per-input disable threaded into each step component. A step's INPUTS are disabled when its
  // part isn't held (edit-only; ⊤/held/duplication => editable); the component leaves its OWN
  // Back/Next/Save nav untouched. Multi-part steps get the partReadOnly(part) fn to gate per sub-control.
  const partReadOnly = (part) => !props.duplicationMode && !partHeld(part);
  const sensorsReadOnly =
    !props.duplicationMode && !partHeld("Monitoring") && !partHeld("Value Insights");
  // D28: does the caller hold at least one part a save can write? (Monitoring & Value
  // Insights share configuredSensors, so either satisfies the sensor part.)
  const holdsAnyPart = [
    partHeld("Key Info"),
    partHeld("Asset"),
    partHeld("Monitoring") || partHeld("Value Insights"),
    partHeld("Controlling"),
    partHeld("Configurations"),
    partHeld("Layout"),
    partHeld("Threshold Management"),
  ].some(Boolean);
  // D28: the save action reaches the steps only when the caller holds a part (or is
  // duplicating, which is full authorship); a step renders its Save only when it gets
  // one, so view-only users see no Save that could only fail.
  const saveAction = props.duplicationMode || holdsAnyPart ? handleSave : undefined;

  function getSteps() {
    return [
      "Key Info",
      "Asset",
      "Monitoring & Controls",
      "Configurations",
      "Layout",
    ];
  }
  // Raw page (0..6) -> visual step (0..4): pages 2/3/4 ALL collapse to the merged
  // "Monitoring & Controls" step (visual 2); Configurations=page5->3, Layout=page6->4.
  // Mirrors the create shell - the middle `: 2` clause maps the
  // Controlling (page 3) and Value-Insights (page 4) sub-tabs back to visual step 2,
  // so the stepper keeps the merged step highlighted (plain p-2 mis-mapped 3->1).
  const pageToVisualStep = (p) => (p <= 2 ? p : p >= 5 ? p - 2 : 2);
  // Inverse (visual step index -> raw page) for the clickable stepper labels.
  const visualStepToPage = (i) => (i <= 2 ? i : i + 2);
  const ColorlibConnector = withStyles({
    alternativeLabel: {
      top: 22,
    },
    active: {
      "& $line": {
        backgroundImage: `linear-gradient( 95deg,${metaDataValue.branding.secondaryColor} 0%,${metaDataValue.branding.secondaryColor} 50%,${metaDataValue.branding.secondaryColor} 100%)`,
      },
    },
    completed: {
      "& $line": {
        backgroundImage: `linear-gradient( 95deg,${metaDataValue.branding.secondaryColor} 0%,${metaDataValue.branding.secondaryColor} 50%,${metaDataValue.branding.secondaryColor} 100%)`,
      },
    },
    line: {
      height: 3,
      border: 0,
      backgroundColor: "#eaeaf0",
      borderRadius: 1,
    },
  })(StepConnector);
  let token = window.localStorage.getItem("token");
  const dispatch = useDispatch();
  const selectedTags = useSelector((state) => state.selectedTags);
  const filteredServices = useSelector((state) => state.filteredServices);
  const [updateServices, updateServicesResult] = useUpdateServicesMutation();
  const [editService, editServiceResult] = useEditServiceMutation();
  const [createService, createServiceResult] = useCreateServiceMutation();
  const { enqueueSnackbar } = useSnackbar();
  const roleRes = useGetSpecificRoleQuery(window.localStorage.getItem("role"), {
    skip: !updateServicesResult.isSuccess,
  });
  // Solution sharing (stage 7): a Duplicate waits for the copy before reloading; not after leaving.
  const mountedRef = React.useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);
  function showSnackbar(title, message, variant, timeOut) {
    return enqueueSnackbar(
      { title, message: message ? message : "Something went wrong", variant },
      { timeOut }
    );
  }

  useEffect(() => {
    if(createServiceResult.isSuccess){
      showSnackbar(
        "Solution",
        "Solution Duplicated Successfully with Defined Settings",
        "success",
        1000
      );
      dispatch(resetService());
      // Solution sharing (plan stage 7): the creation worker writes the creator's role last, so wait
      // (at most 2 minutes) until the role holds the copy before the reload reads the role again; no
      // reload if the user has left this page meanwhile.
      const copyId = createServiceResult.data?.payload?._id;
      const reload = () => {
        if (!mountedRef.current) return;
        props.history.push("/solutionManagement/solutionEdit");
        window.location.reload();
      };
      if (copyId) {
        showSnackbar("Solution", "Finishing the copy...", "info", 3000);
        waitForSolutionInRole(dispatch, window.localStorage.getItem("role"), copyId).then(reload);
      } else reload();
    }
    if (createServiceResult.isError) {
      showSnackbar(
        "Solution",
        createServiceResult.error?.data?.message,
        "error",
        1000
      );
      dispatch(
        setService({
          page: 0,
        })
      );
    }
  }, [createServiceResult])

  useEffect(() => {
    if (editServiceResult.isSuccess) {
      // P2c: the role per-service feature-tab sync is DERIVED from metaData (Key Info). If the caller
      // does not hold Key Info, handleSave dropped body.metaData so the BE left the service featureTabs
      // unchanged — skip this role-tab mutation too, else the role desyncs from the service. ⊤ holds all
      // parts via partHeld; duplication uses createService (not this editService effect), so it never
      // reaches here.
      if (partHeld("Key Info")) {
        let tempTabs = {
          location: serviceValue.metaData.location,
          maintenance: serviceValue.metaData.maintenance,
          videoAnalytics: serviceValue.metaData.videoAnalytics,
          digitalTwin: serviceValue.metaData.digitalTwin,
        };

        let dynamicTabs = [
          { key: "maintenance", name: "Rules" },
          { key: "location", name: "Tracking" },
          { key: "videoAnalytics", name: "Video Analytics" },
          { key: "digitalTwin", name: "Digital Twin" },
        ];
        let tabs = metaDataValue.services.find(
          (s) => s.id == serviceValue.serviceId
        ).tabs;
        let serviceTabs = [];
        dynamicTabs.forEach((r) => {
          if (!tabs.find((t) => t.name == r.name) && tempTabs[r.key] == true) {
            serviceTabs.push({
              name: r.name,
              permission: "ALL",
              addFlag: "true",
            });
          }
          if (tabs.find((t) => t.name == r.name) && tempTabs[r.key] == false) {
            serviceTabs.push({ name: r.name, addFlag: "false" });
          }
        });
        let body = {
          service: {
            serviceId: serviceValue.serviceId,
            tabs: serviceTabs,
          },
        };
        updateServiceForRoles(body);
      }

      showSnackbar(
        "Solution",
        editServiceResult.data?.message,
        "success",
        1000
      );
      dispatch(resetService());
      props.history.push("/solutionManagement/solutionEdit");
      window.location.reload()
      //
    }
    if (editServiceResult.isError) {
      showSnackbar(
        "Solution",
        editServiceResult.error?.data?.message,
        "error",
        1000
      );
      dispatch(
        setService({
          page: 0,
        })
      );
    }
  }, [editServiceResult]);

  async function updateServiceForRoles(body) {
    let updated = await updateServices({
      token,
      id: window.localStorage.getItem("role"),
      body,
    });
  }

  useEffect(() => {
    if (roleRes.isSuccess) {
      dispatch(setRole(roleRes.data.payload));
      props.setSelected(null);
    }
  }, [roleRes.isFetching]);

  useEffect(() => {
    return () => {
      dispatch(resetService());
    };
  }, []);
  const modifyAssetMapping = (assetMappingArray) => {
    return assetMappingArray.map((item) => {
      // Modify the assetType property
      const modifiedItem = {
        assetId: item.assetType._id,
        actuators: item.actuators.map((obj) => obj._id),
        sensors: item.sensors
          ? item.sensors
              .filter((sensor) => sensor.typeValue === "datapoint")
              .map((datapointSensor) => datapointSensor._id)
          : [], // Filter sensors with typeValue "datapoint"
        valueInsights: item.sensors
          ? item.sensors
              .filter((sensor) => sensor.typeValue === "valueInsight")
              .map((datapointSensor) => datapointSensor._id)
          : [], // Add the valueInsights property
      };

      return modifiedItem;
    });
  };
  const modifyAssetToMultipleAssets = (
    assetId,
    actuators,
    sensors,
    valueInsights
  ) => {
    const modifiedItem = {
      assetId: assetId,
      actuators: actuators,
      sensors: sensors
        ? sensors
            .filter((sensor) => sensor.typeValue === "datapoint")
            .map((datapointSensor) => datapointSensor._id)
        : [], // Filter sensors with typeValue "datapoint"
      valueInsights: sensors
        ? sensors
            .filter((sensor) => sensor.typeValue === "valueInsight")
            .map((datapointSensor) => datapointSensor._id)
        : [],
    };
    return [modifiedItem];
  };
  useEffect(() => {
    // console.log("props", props);

    if (props.selected) {
      let configuredAssets = [];
      if (
        props.selected?.configuredAssets &&
        props.selected?.configuredAssets.length
      ) {
        configuredAssets = props.selected?.configuredAssets;
      }
      let configuredActuators = [];
      if (props.selected?.configuredActuators) {
        configuredActuators = props.selected?.configuredActuators;
      }
      let configuredSensors = [];
      if (props.selected?.configuredSensors) {
        configuredSensors = props.selected?.configuredSensors;
      }
      let assetMapping = [];
      if (props.selected?.assetMapping && props.selected?.assetMapping.length) {
        assetMapping = modifyAssetMapping(props.selected?.assetMapping);
      }
      // console.log(props.selected);
      let tags = [];
      let meta = [];
      props.selected.tags.forEach((elm) => {
        tags.push(elm._id);
      });
      props.selected.metaTags?.forEach((elm) => {
        meta.push(elm._id);
      });
      let actuator = [];
      props.selected.configuredActuators.forEach((elm) => {
        actuator.push(elm._id);
      });
      let sensors = [];
      props.selected.configuredSensors.forEach((elm) => {
        sensors.push(elm._id);
      });
      let datapoints = [];
      props.selected.configuredSensors.forEach((elm) => {
        datapoints.push({
          id: elm._id,
          name: elm.name,
          friendlyName: elm.friendlyName,
        });
      });
      let dataPointThresholds = [];
      if (props.selected?.dataPointThresholds)
        dataPointThresholds = props.selected.dataPointThresholds.map((e) => {
          let res = { ...e };
          res.dataPoint = e.dataPoint?._id;
          return res;
        });
      let metaData;
      if (typeof props.selected.metaData === "string")
        metaData = JSON.parse(props.selected.metaData);
      else metaData = props.selected.metaData;
      let tabs = metaData.tabs;
      let groupMetaData = props.selected?.groupMetaData ? props.selected?.groupMetaData : []
      dispatch(
        setService({
          serviceId: props.duplicationMode ? "" : props.selected._id,
          name: props.duplicationMode ? `Clone - ${props.selected.name}` : props.selected.name,
          duration: props.selected?.duration,
          vanish: props.selected?.vanish ? props.selected?.vanish : false,
          devPrompt: props.selected?.devicePrompt
            ? props.selected?.devicePrompt
            : false,
          description: props.selected.description,
          parentChildEnabled: props.selected.parentChildEnabled,
          tags: tags,
          cover: props.selected.logoPath,
          svg: tabs.digitalTwin.svg,
          meta,
          asset: configuredAssets.length
            ? configuredAssets.map((obj) => obj._id)
            : [props.selected.configuredAsset._id],

          actuator: actuator,
          configuredAssets: configuredAssets.length
            ? configuredAssets
            : [props.selected.configuredAsset._id],
          configuredActuators: configuredActuators,
          configuredSensors: configuredSensors,
          assetMapping: assetMapping.length
            ? assetMapping
            : modifyAssetToMultipleAssets(
                props.selected.configuredAsset._id,
                actuator,
                props.selected.configuredSensors
              ),
          defaultLocation: props.selected.defaultLocation,
          monitoring: sensors,
          datapoints: datapoints,
          widgetDatapoints: props.selected.widgetDatapoints || {},
          dataPointThresholds: dataPointThresholds,
          metaData: {
            location: tabs.location,
            maintenance: tabs.maintenance,
            videoAnalytics: tabs.videoAnalytics,
            digitalTwin: tabs.digitalTwin.value,
          },
          groupMetaData: groupMetaData,
          map: props.selected?.layout?.map
            ? props.selected?.layout?.map
            : serviceValue.map,
          layout: props.selected.layout?.dashboardView || serviceValue.layout,
          trend: props.selected.trend,
          persist: {
            ...serviceValue.persist,
            tags: [],
            meta: props.selected.metaTags,
            cover: props.selected.logoPath
              ? `${Keys.baseUrl}/servicecreator/service/${props.selected.logoPath}`
              : null,
          },
          energySavingEnable: (props.selected?.layout?.dashboardView == 1 || serviceValue.layout == 1) ? props.selected?.energySavingEnable : false,
          currency: (props.selected?.layout?.dashboardView == 1 || serviceValue.layout == 1) ? props.selected?.currency : "GBP",
          multiplier: (props.selected?.layout?.dashboardView == 1 || serviceValue.layout == 1) ? props.selected?.multiplier : 1,
          temperatureUnit: (props.selected?.layout?.dashboardView == 1 || serviceValue.layout == 1) ? props.selected?.temperatureUnit : 'celsius',
        })
      );
      setLoader(false);
    }
  }, [props.selected]);

  const classes = useStyles();
  const [loader, setLoader] = React.useState(true);

  const steps = getSteps();

  function getStepContent(step) {
    switch (step) {
      case 0:
        return (
          <Details
            edit
            readOnly={partReadOnly("Key Info")}
            setSelected={props.setSelected}
            handleSave={saveAction}
          />
        );
      case 1:
        return (
          <AssetComp
            edit
            readOnly={partReadOnly("Asset")}
            setSelected={props.setSelected}
            handleSave={saveAction}
          />
        );
      case 2:
      case 3:
      case 4:
        return (
          <MonitoringControls
            edit
            sensorsReadOnly={sensorsReadOnly}
            controllingReadOnly={partReadOnly("Controlling")}
            setSelected={props.setSelected}
            handleSave={saveAction}
          />
        );
      case 5:
        return (
          <Dashboard
            edit
            partReadOnly={partReadOnly}
            sensors={props.sensors}
            handleSave={saveAction}
            id={props.selected._id}
          />
        );
      case 6:
        return (
          <Layout
            edit
            partReadOnly={partReadOnly}
            sensors={props.sensors}
            handleSave={saveAction}
            id={props.selected._id}
            dashboardView={props?.selected?.layout?.dashboardView}
            widgetDatapoints={props?.selected?.widgetDatapoints}
          />
        );
      default:
        return null;
    }
  }

  async function handleSave() {
    // D28: the content checks run once, here (the steps no longer pre-check in edit mode),
    // and only when the caller holds a part that can fix them - datapoints need Monitoring
    // or Value Insights, a Monitoring/Controlling parameter also accepts Controlling.
    // Duplication is full authorship (both read-only flags are false), so both run.
    const sensorsHeld = !sensorsReadOnly;
    const controllingHeld = !partReadOnly("Controlling");
    // Validate >=1 datapoint per asset (reads the pre-transform mapping).
    if (sensorsHeld) {
      const assetMapping = JSON.parse(JSON.stringify([...serviceValue.assetMapping]));
      let missingAssets = false;
      serviceValue.asset.forEach((assetId) => {
        const matchingAsset = assetMapping.find((asset) => asset.assetId === assetId);
        if (!matchingAsset || !matchingAsset.sensors || matchingAsset.sensors.length === 0) {
          if (!missingAssets) {
            missingAssets = true;
            showSnackbar("Solution", "Please select atleast 1 datapoint for each asset", "error", 1000);
          }
          return;
        }
      });
      if (missingAssets) return;
    }
    if (
      (sensorsHeld || controllingHeld) &&
      serviceValue.actuator.length < 1 &&
      serviceValue.monitoring.length < 1
    ) {
      showSnackbar("Solution", "Select atleast one Monitoring/Controlling parameter", "info", 1000);
      return;
    }

    // Unified body-builder (shell-unify): edit mode; part-filtered unless duplicating.
    const body = buildServicePayload(serviceValue, {
      mode: "edit",
      partHeld: props.duplicationMode ? undefined : partHeld,
    });

    // C1: if the per-part filter stripped the body to nothing (a role holding Solution Edit=ALL
    // but zero part-tabs), don't fire a misleading no-op editService({}) — warn and bail so the toast
    // matches the read-only banner (mirrors the BE fail-loud intent client-side).
    if (!props.duplicationMode && Object.keys(body).length === 0) {
      showSnackbar("Solution", "Your role has no editable parts for this solution", "error", 1000);
      return;
    }

      if(props.duplicationMode){
        createService({
          token,
          body
        })
      } else {
        editService({
          token,
          id: serviceValue.serviceId,
          body,
        });
      }
  }

  const detailText = {
    0: "Please fill out the required info and select the features you want to enable in the solution.",
    1: "Define appropriate image and name for your asset.",
    2: "Select data from the catalogue or create new data points that this solution should monitor.",
    3: "Select control features from the catalogue or create new ones that this solution should offer.",
    4: "Set Solution Dashboard settings.",
  };

  return (
    <Fragment>
      {loader ? null : (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
          }}
        >
          <Card style={{ width: "100%", padding: "20px" }}>
            <div className={classes.root}>
              <Stepper
                alternativeLabel
                activeStep={pageToVisualStep(serviceValue.page)}
                connector={<ColorlibConnector />}
              >
                {steps.map((label, stepIndex) => (
                  <Step key={label}>
                    {/* Edit mode: the solution is fully hydrated, so any step can
                        be opened directly — jump on click instead of clicking
                        Next/Back through every step. */}
                    <StepLabel
                      StepIconComponent={ColorlibStepIcon}
                      onClick={() =>
                        dispatch(setService({ page: visualStepToPage(stepIndex) }))
                      }
                      style={{ cursor: "pointer" }}
                    >
                      {label != "Key Info" ? (
                        <p>{label}</p>
                      ) : (
                        <div>
                          <p>Key Info</p>
                          <p style={{ color: "#cccccc" }}>
                            {serviceValue.name != ""
                              ? `(${serviceValue.name})`
                              : ""}
                          </p>
                        </div>
                      )}
                    </StepLabel>
                  </Step>
                ))}
              </Stepper>
              {detailText[serviceValue.page] ? (
                <div
                  style={{
                    position: "absolute",
                    right: "185px",
                    color: "#555555",
                    cursor: "pointer",
                  }}
                >
                  <Tooltip
                    title={detailText[serviceValue.page]}
                    placement="left"
                    arrow
                    TransitionComponent={Zoom}
                  >
                    <InfoIcon />
                  </Tooltip>
                </div>
              ) : null}

              {currentStepReadOnly ? (
                <div
                  style={{
                    margin: "8px 0",
                    padding: "8px 12px",
                    borderRadius: 4,
                    background: "#fff3e0",
                    color: "#8a6d3b",
                    border: "1px solid #ffe0b2",
                    fontSize: 13,
                  }}
                >
                  {holdsAnyPart
                    ? "Your role has edit access to only some parts of a solution. Any change you make to a part your role doesn't include will not be saved."
                    : "Your role has view-only access to this solution. Changes cannot be saved."}
                </div>
              ) : null}
              <div>{getStepContent(serviceValue.page)}</div>
            </div>
          </Card>
        </div>
      )}
    </Fragment>
  );
}
